import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Lock, CheckCircle2, Circle, Pencil, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Overline, Loading, LockCard, ProgressBar } from "@/components/shared";
import { SystemStrip } from "@/components/SystemStrip";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const PART_META = {
  part_1: { name: "The Role", kicker: "Toolkit 1 · Management 101", tagline: "Understand the job.", plum: false },
  part_2: { name: "The People", kicker: "Toolkit 2 · The People Work", tagline: "Lead people intentionally.", plum: true },
};

export default function PartSection() {
  const { part } = useParams();
  const { entitlements, user } = useAuth();
  const { setPart2 } = useTheme();
  const [searchParams] = useSearchParams();
  const openParam = searchParams.get("open");
  const [sections, setSections] = useState(null);
  const [progress, setProgress] = useState({ completed: [] });
  const [openItems, setOpenItems] = useState([]);
  const [intro, setIntro] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const meta = PART_META[part] || PART_META.part_1;
  const unlocked = part === "part_1" ? entitlements.has_part_1 : entitlements.has_part_2;

  useEffect(() => {
    setPart2(part === "part_2");
    api.get("/content/sections").then((r) => setSections(r.data.filter((s) => s.part === part)));
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
    api.get(`/content/part-overview/${part}`).then((r) => setIntro(r.data.intro || "")).catch(() => setIntro(""));
    return () => setPart2(false);
  }, [part, setPart2]);

  const saveIntro = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/part-overview/${part}`, { intro: draft });
      setIntro(draft); setEditing(false);
      toast.success("Overview intro saved");
    } catch { toast.error("Could not save intro"); }
    setSaving(false);
  };

  useEffect(() => {
    if (!sections) return;
    const valid = sections.map((s) => s.slug);
    setOpenItems(openParam && valid.includes(openParam) ? [openParam] : (valid.length ? [valid[0]] : []));
    if (openParam && valid.includes(openParam)) {
      setTimeout(() => document.getElementById(`sec-${openParam}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    }
  }, [openParam, sections]);

  if (!sections) return <Loading />;
  const done = new Set(progress.completed || []);

  return (
    <div data-testid="part-section-page">
      <SystemStrip current={part} plum={meta.plum} />
      <Overline className={meta.plum ? "text-nm-green" : ""}>{meta.kicker}</Overline>
      <h1 className={`font-serif text-4xl sm:text-5xl mt-2 leading-tight ${meta.plum ? "text-nm-plum" : "text-nm-ink"}`}>{meta.name}</h1>
      <p className={`font-serif italic text-2xl mt-3 ${meta.plum ? "text-nm-green" : "text-nm-primary"}`}>{meta.tagline}</p>

      {editing ? (
        <div className="mt-5 max-w-none" data-testid="overview-editor">
          <textarea
            value={draft} onChange={(e) => setDraft(e.target.value)} rows={5}
            data-testid="overview-intro-input"
            className={`w-full rounded-xl border bg-white/80 p-4 text-[17px] text-nm-ink leading-relaxed focus:outline-none focus:ring-2 ${meta.plum ? "border-nm-plum/25 focus:ring-nm-plum/40" : "border-nm-primary/20 focus:ring-nm-primary/40"}`}
            placeholder="Write a short intro for this part…" />
          <div className="mt-2 flex items-center gap-3">
            <button onClick={saveIntro} disabled={saving} data-testid="overview-save-btn"
              className={`rounded-full px-5 py-2 text-sm font-medium text-white disabled:opacity-60 ${meta.plum ? "bg-nm-plum" : "bg-nm-primary"} hover:brightness-105 transition`}>
              {saving ? "Saving…" : "Save intro"}
            </button>
            <button onClick={() => setEditing(false)} data-testid="overview-cancel-btn"
              className="inline-flex items-center gap-1 text-sm text-nm-neutral hover:text-nm-ink transition">
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4 max-w-none group relative">
          {(intro || "").split("\n").filter((l) => l.trim()).map((line, i) => (
            <p key={i} className="text-lg text-nm-neutral font-light leading-relaxed mt-2" data-testid={`overview-intro-p-${i}`}>
              {line.split(/(\*\*[^*]+\*\*)/g).map((seg, k) =>
                seg.startsWith("**") && seg.endsWith("**")
                  ? <strong key={k} className="font-semibold text-nm-ink">{seg.slice(2, -2)}</strong>
                  : seg
              )}
            </p>
          ))}
          {user?.is_admin && (
            <button onClick={() => { setDraft(intro); setEditing(true); }} data-testid="overview-edit-btn"
              className={`mt-3 inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${meta.plum ? "border-nm-plum/40 text-nm-plum hover:bg-nm-plum/10" : "border-nm-coral/40 text-nm-coral hover:bg-nm-coral/10"}`}>
              <Pencil size={13} /> Edit intro
            </button>
          )}
        </div>
      )}

      {!unlocked ? (
        <div className="mt-10">
          <LockCard part={part}
            teaser={part === "part_2"
              ? "The Human Side of Management is visible from day one and unlocks when you're ready. Preview every module below."
              : "Part 1 is the foundation of the operating system. Unlock it to open every chapter and template below."} />
          <div className="mt-10 space-y-10">
            {sections.map((s) => (
              <div key={s.slug}>
                <h2 className={`font-serif text-2xl mb-4 ${meta.plum ? "text-nm-plum" : "text-nm-ink"}`}>{s.title}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {s.chapters.map((c) => (
                    <PreviewCard key={c.slug} c={c} plum={meta.plum} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <Accordion type="multiple" value={openItems} onValueChange={setOpenItems} className="mt-10 space-y-4">
          {sections.map((s) => {
            const doneCount = s.chapters.filter((c) => done.has(c.slug)).length;
            const pct = s.chapter_count ? Math.round((doneCount / s.chapter_count) * 100) : 0;
            const accent = meta.plum ? "text-nm-green" : "text-nm-coral";
            const rowHover = meta.plum ? "hover:bg-nm-plum/[0.04]" : "hover:bg-nm-primary/[0.04]";
            return (
              <AccordionItem value={s.slug} key={s.slug} id={`sec-${s.slug}`}
                className={`border rounded-2xl bg-nm-surface nm-shadow-sm overflow-hidden scroll-mt-24 ${meta.plum ? "border-nm-plum/12" : "border-nm-primary/10"}`}>
                <AccordionTrigger className={`px-5 sm:px-6 py-5 hover:no-underline ${rowHover} transition-colors duration-200`} data-testid={`section-toggle-${s.slug}`}>
                  <div className="flex items-center gap-4 flex-1 min-w-0 text-left pr-3">
                    <div className="flex-1 min-w-0">
                      <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${accent}`}>Section · {doneCount}/{s.chapter_count}</div>
                      <h2 className={`font-serif text-xl sm:text-2xl mt-0.5 leading-snug ${meta.plum ? "text-nm-plum" : "text-nm-ink"}`}>{s.title}</h2>
                    </div>
                    <div className="hidden sm:flex flex-col items-end w-28 shrink-0">
                      <span className="text-xs text-nm-neutral mb-1.5">{pct}%</span>
                      <ProgressBar pct={pct} plum={meta.plum} />
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className={`border-t ${meta.plum ? "border-nm-plum/10" : "border-nm-primary/8"}`}>
                    {s.chapters.map((c, ci) => (
                      <Link key={c.slug} to={`/app/chapter/${c.slug}`} data-testid={`chapter-link-${c.slug}`}
                        className={`group flex items-center gap-4 px-5 sm:px-6 py-4 ${ci > 0 ? (meta.plum ? "border-t border-nm-plum/8" : "border-t border-nm-primary/6") : ""} ${rowHover} transition-colors duration-200`}>
                        {done.has(c.slug)
                          ? <CheckCircle2 size={18} className="text-nm-green shrink-0" />
                          : <Circle size={18} className={`${meta.plum ? "text-nm-plum/30" : "text-nm-primary/30"} shrink-0`} />}
                        <div className="flex-1 min-w-0">
                          <div className="font-serif text-lg text-nm-ink leading-snug">{c.title}</div>
                          <p className="text-sm text-nm-neutral mt-0.5 leading-relaxed line-clamp-1">{c.summary}</p>
                        </div>
                        <ArrowRight size={16} className={`${meta.plum ? "text-nm-plum/50" : "text-nm-primary/50"} shrink-0 group-hover:translate-x-1 transition-transform duration-200`} />
                      </Link>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </div>
  );
}

function PreviewCard({ c, plum }) {
  return (
    <div className={`relative rounded-2xl border p-5 ${plum ? "border-nm-plum/15 bg-nm-plum/[0.03]" : "border-nm-primary/10 bg-nm-primary/[0.02]"}`} data-testid={`preview-${c.slug}`}>
      <div className="flex items-start justify-between">
        <div className="font-serif text-lg text-nm-ink leading-snug pr-6">{c.title}</div>
        <Lock size={15} className={plum ? "text-nm-plum/60" : "text-nm-primary/50"} />
      </div>
      <p className="text-sm text-nm-neutral mt-1.5 leading-relaxed line-clamp-2">{c.summary}</p>
    </div>
  );
}
